import { queryOptions } from "@tanstack/react-query";
import { quickActionsKeys } from "./keys";
import { apiClient } from "~/lib/client";
import { GetPatientMedicationPlanResponse, GetPatientMedicationResponse, GetPatientNotesResponse, GetPatientPaymentHistoryResponse, GetPatientQuestionnaireResponse, GetPatientSummaryResponse, GetPatientTimelineResponse } from "../types";
import { handleApiError, handleResponse } from "~/utils/response";
import { buildQueryString } from "~/utils/url";


export interface BuildQueryParams {
    skip: number
    limit: number
    sort_by_closest: boolean
    [key: string]: string | number | boolean
}


interface GetPatientDetailsByIdDetails {
    patient_id: string;
}



export const getPatientWeightSummary = (options: GetPatientDetailsByIdDetails) => {
    return queryOptions({
        queryKey: quickActionsKeys.patientLogById(options.patient_id),
        retry: false,
        queryFn: async () => {
            try {
                const response = await apiClient(`/admin/patients/${options.patient_id}`);

                const result = await handleResponse<GetPatientSummaryResponse>(response)

                if (!result.success) {
                    throw new Error(result.error)
                }

                return result.data
            } catch (error) {
                console.error("Error fetching weight summary by patient ID:", error);
                handleApiError(error);
                throw error;
            }
        },

        select(data) {
            return {
                userId: data?.patient_summary?.user_id || '',
                age: data?.patient_summary?.age || 0,
                gender: data.patient_summary?.gender || '',
                initialWeight: data?.patient_summary?.initial_weight || 0,
                goalWeight: data?.patient_summary?.target_weight || 0,
            }
        }
    })
}


export const getPatientTimeline = (opts: GetPatientDetailsByIdDetails)=> {
    return queryOptions({
        queryKey: quickActionsKeys.patientLogByTimeline(opts.patient_id),
        retry: false,
        queryFn: async () => {
            try {
                const response = await apiClient(`/admin/patients/${opts.patient_id}/timeline`);

                const result = await handleResponse<GetPatientTimelineResponse>(response)

                if (!result.success) {
                    throw new Error(result.error)
                }

                return result.data
            } catch (error) {
                console.error("Error fetching appointment by patient ID:", error);
                handleApiError(error);
                throw error;
            }
        },

        select(data) {
            return {
                status: data?.status || '',
                message: data?.message || '',
                timeline: data?.timeline?.map(timeline => {
                    return {
                        event_type: timeline.event_type || "Enrollment in Weight Loss Program",
                        date: formatDate(timeline.date, "date"),
                        time: timeline.time || "10:00 AM",
                        description: timeline.description || "Patient completed initial questionnaire and enrolled in the 6-month weight loss program.",
                        id: timeline.id
                    };
                }) || [],
            };
        }
    })
}


export const getPatientMedicationQuery = (opts: GetPatientDetailsByIdDetails)=> {
    return queryOptions({
        queryKey: quickActionsKeys.patientLogByMedication(opts.patient_id),
        retry: false,
        queryFn: async ()=> {
            try {
                const response = await apiClient(`/admin/patients/${opts.patient_id}/questionnaire`);

                const result = await handleResponse<GetPatientMedicationResponse>(response)

                if (!result.success) {
                    throw new Error(result.error)
                }

                return result.data
            } catch (error) {
                console.error("Error fetching questionnaire by patient ID:", error);
                handleApiError(error);
                throw error;
            } 
        },
        select(data) {
            return {
                ...data,
                refill_requests: data?.refill_requests?.map(request => ({
                    date: request.date || 'Lisinopril',
                    price: request.amount || '$50.00',
                    requested_date: request.date || '3/20/2025',
                    status: request.status || 'Processing',
                    actions: 'View'
                })) || [],
                current_medications: data?.current_medications?.map(med => ({
                    medication: med.description || 'Lisinopril, 37.5mg daily',
                    price: med.amount || '$50.00',
                    prescribed_date: med.date || '3/20/2025',
                    refills: '2',
                    status: med.status || 'Active'
                })) || [],
                medication_refill_history: data?.medication_history?.map(history => ({
                    medication: history.description || 'Lisinopril, 37.5mg daily',
                    date: history.date || '3/20/2025',
                    status: history.status || 'Completed'
                })) || []
            };
        }
    })
}



export const getPatientQuestionnaireQuery = (opts: GetPatientDetailsByIdDetails) => {
    return queryOptions({
        queryKey: quickActionsKeys.patientLogByQuestionaire(opts.patient_id),
        retry: false,
        queryFn: async () => {
            try {
                const response = await apiClient(`/admin/patients/${opts.patient_id}/questionnaire`);

                const result = await handleResponse<GetPatientQuestionnaireResponse>(response)

                if (!result.success) {
                    throw new Error(result.error)
                }

                return result.data
            } catch (error) {
                console.error("Error fetching questionnaire by patient ID:", error);
                handleApiError(error);
                throw error;
            }
        }
        
    })
}



export const getPatientMedicalPlanQuery = (opts: GetPatientDetailsByIdDetails) => {
    return queryOptions({
        queryKey: quickActionsKeys.patientLogByMedicalPlan(opts.patient_id),
        retry: false,
        queryFn: async () => {
            try {
                const response = await apiClient(`/admin/patients/${opts.patient_id}/medical-overview`);

                const result = await handleResponse<GetPatientMedicationPlanResponse>(response)

                if (!result.success) {
                    throw new Error(result.error)
                }

                return result.data
            } catch (error) {
                console.error("Error fetching patient medical plan by patient ID:", error);
                handleApiError(error);
                throw error;
            }
        },

        select(data) {
            return {
                status: data?.status || '',
                message: data?.message || '',
                program_details: {
                    program: data?.program_details?.program || '6-Month Medical Weight Loss',
                    start_date: data?.program_details?.start_date || 'July 15, 2025',
                    end_date: data?.program_details?.end_date || 'July 15, 2025'
                },
                appointment_schedule: {
                    initial_consultation: {
                        status: data?.appointment_schedule?.initial_consultation?.status || 'Completed',
                        date: data?.appointment_schedule?.initial_consultation?.date || '3/20/2025'
                    },
                    follow_ups: data?.appointment_schedule?.follow_ups || []
                },
                medication_plan: data?.medication_plan || [
                    {
                        medication: 'Lisinopril, 37.5mg daily',
                        price: '$50.00',
                        prescribed_date: '3/20/2025',
                        refills: '2',
                        status: 'Active'
                    }
                ],
                conditions_and_allergies: {
                    allergies: data?.conditions_and_allergies?.allergies || [
                        {
                            allergy: 'Peanuts',
                            status: 'Severe'
                        },
                        {
                            allergy: 'Pollen',
                            status: 'Mild'
                        },
                        {
                            allergy: 'Dust Mites',
                            status: 'Mild'
                        }
                    ],
                    health_conditions: data?.conditions_and_allergies?.health_conditions || [
                        {
                            condition: 'High Blood Pressure',
                            status: 'Severe'
                        },
                        {
                            condition: 'Type 2 Diabetes',
                            status: 'Mild'
                        },
                        {
                            condition: 'Asthma',
                            status: 'Mild'
                        }
                    ]
                }
            };
        }
    })
}

export const getPatientPaymentHistoryQuery = ({opts, params}: {opts: GetPatientDetailsByIdDetails, params: BuildQueryParams}) => {
    return queryOptions({
        queryKey: quickActionsKeys.patientLogByPaymentHistory(opts.patient_id, params),
        retry: false,
        queryFn: async () => {
            try {
                const response = await apiClient(`/admin/patients/${opts.patient_id}/payment-history${buildQueryString(params)}`);

                const result = await handleResponse<GetPatientPaymentHistoryResponse>(response)

                if (!result.success) {
                    throw new Error(result.error)
                }

                return result.data
            } catch (error) {
                console.error("Error fetching payment history by patient ID:", error);
                handleApiError(error);
                throw error;
            }
        }
    })
}



export const getPatientNotesQuery = ({opts, params}: {opts: GetPatientDetailsByIdDetails, params: BuildQueryParams}) => {
    return queryOptions({
        queryKey: quickActionsKeys.patientLogByNotes(opts.patient_id, params),
        retry: false,
        queryFn: async () => {
            try {
                const response = await apiClient(`/admin/patients/${opts.patient_id}/notes${buildQueryString(params)}`);

                const result = await handleResponse<GetPatientNotesResponse>(response)

                if (!result.success) {
                    throw new Error(result.error)
                }

                return result.data
            } catch (error) {
                console.error("Error fetching notes by patient ID:", error);
                handleApiError(error);
                throw error;
            }
        }
    })
}
